import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Separator } from '@/components/ui/separator'

import { LoadingSpinner } from '@/components/LoadingSpinner.tsx'
import { ProjectCard } from '@/components/ProjectCard.tsx'
import { useUserContext } from '@/contexts/UserContext'
import { createProject } from '@/repositories/projectRepository.ts'
import { useQueryAllProjectsLimitedIterations } from '@/requests/queries/useQueryAnalytics'
import { useQueryProjects } from '@/requests/queries/useQueryProjects.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'

export const ProjectsPage = () => {
  const { projectsMeta, loading: projectsLoading, error, refetch } = useQueryProjects()
  const { apiKey } = useUserContext()
  const { iterations, loading: iterationsLoading } = useQueryAllProjectsLimitedIterations(apiKey)

  const projectIterationsMap = useMemo(() => {
    const map = new Map<string, ProjectCardIteration[]>()

    for (const iteration of iterations) {
      const projectId = iteration.projectId
      if (!map.has(projectId)) {
        map.set(projectId, [])
      }
      map.get(projectId)?.push(iteration as ProjectCardIteration)
    }

    return map
  }, [iterations])

  if (error) {
    return <div>Error loading projects meta info</div>
  }

  if (projectsLoading)
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )

  return (
    <div className="bg-surface-background">
      <main className="flex flex-col items-center py-6 px-4 max-w-[1078px] mx-auto">
        <div className="flex items-center justify-between w-full mb-4">
          <h1 className="text-[20px] font-bold text-contents-primary leading-[30px]">My Project</h1>

          <CreateProjectModal createCallback={refetch} />
        </div>

        {projectsMeta.length === 0 ? (
          <Card className="w-full border-dashed border-border-default-secondary bg-surface-backgroundElevated">
            <CardContent className="flex items-center justify-center py-12">
              <p className="text-[16px] text-contents-secondary leading-[24px]">
                You’re not a member of any project yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="w-full bg-surface-background">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {projectsMeta.map((projectMeta: ProjectMeta) => (
                <ProjectCard
                  key={projectMeta.id}
                  project={projectMeta}
                  iterations={projectIterationsMap.get(projectMeta.id) || []}
                  loading={iterationsLoading}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

type CreateProjectModalProps = {
  createCallback: () => void
}

const CreateProjectModal = ({ createCallback }: CreateProjectModalProps) => {
  const navigate = useNavigate()

  const [projectName, setProjectName] = useState('')

  const onSubmit = () => {
    if (projectName.trim().length === 0) {
      return
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Tokyo'

    createProject(projectName, timeZone).then((project) => {
      createCallback()
      navigate(`/project/${project.id}`)
    })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="primary" className="font-EN-KR-body1-bold">
          Create a Project
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px] h-[400px] p-0 flex flex-col bg-surface-backgroundElevated">
        <DialogHeader className="px-4 pt-4">
          {/* todo: update color */}
          <DialogTitle className="text-contents-primary font-EN-KR-sub-heading-bold">Create a new project</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <Separator className="mb-4" />

        <div className="flex flex-col gap-2 px-4 flex-grow justify-center">
          <Label htmlFor="project-name" className="font-EN-KR-body2-regular text-contents-primary">
            Project name
          </Label>
          <Input
            id="project-name"
            placeholder="Enter a name for your project"
            value={projectName}
            onChange={(e) => {
              if (e.target.value.length > 40) {
                return
              }
              setProjectName(e.target.value)
            }}
            onKeyDown={(e) => onEnterInclusive(e, onSubmit)}
          />
          <p className="text-contents-secondary font-EN-KR-body2-regular">
            The project name can be up to 40 characters
          </p>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between p-4">
          <DialogClose asChild>
            <Button variant="outline" className="h-9 font-EN-KR-body1-regular">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            disabled={projectName.trim().length === 0}
            className="h-9 font-EN-KR-body1-bold"
            onClick={onSubmit}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
